import { useCallback, useEffect } from 'react'
import { create } from 'zustand'

export const useSearchParamsStore = create<{
  searchParams: URLSearchParams
  setSearchParams: (newState: URLSearchParams) => void
  clearSearchParams: () => void
}>((set) => ({
  searchParams: new URLSearchParams(window.location.search),
  setSearchParams: (searchParams) => set({ searchParams }),
  clearSearchParams: () =>
    set({
      searchParams: new URLSearchParams(),
    }),
}))

export const useSearchParams = () => {
  const { setSearchParams, clearSearchParams } = useSearchParamsStore(
    (state) => state,
  )

  const searchParams = useSearchParamsStore((state) => state.searchParams)

  const applyNewParams = useCallback(
    <T extends Record<string, unknown>>(
      currentParams: URLSearchParams,
      updates: T,
    ) => {
      const newParams = new URLSearchParams(currentParams)
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) {
          newParams.delete(key)
        } else {
          newParams.set(key, String(value))
        }
      })
      return newParams
    },
    [],
  )

  const push = useCallback(
    <T extends Record<string, unknown>>(updates: T) => {
      // Always get the latest searchParams from the store
      const currentParams = useSearchParamsStore.getState().searchParams

      const urlHash = window.location.hash ?? ''
      const newParams = applyNewParams(currentParams, updates)

      window.history.pushState(
        {},
        '',
        newParams.size === 0
          ? `${window.location.pathname}${urlHash}`
          : `${window.location.pathname}${urlHash}?${newParams.toString()}`,
      )
      setSearchParams(newParams)
    },
    [applyNewParams],
  )

  const replace = useCallback(
    <T extends Record<string, unknown>>(updates: T) => {
      // Always get the latest searchParams from the store
      const currentParams = useSearchParamsStore.getState().searchParams

      const urlHash = window.location.hash ?? ''
      const newParams = applyNewParams(currentParams, updates)

      window.history.replaceState(
        {},
        '',
        newParams.size === 0
          ? `${window.location.pathname}${urlHash}`
          : `${window.location.pathname}${urlHash}?${newParams.toString()}`,
      )
      setSearchParams(newParams)
    },
    [applyNewParams],
  )

  const setParamsFromUrl = useCallback(() => {
    setSearchParams(
      applyNewParams(new URLSearchParams(window.location.search), {}),
    )
  }, [applyNewParams])

  useEffect(() => {
    window.addEventListener('popstate', setParamsFromUrl)
    return () => {
      window.removeEventListener('popstate', setParamsFromUrl)
    }
  }, [applyNewParams])

  return {
    setParamsFromUrl,
    searchParams,
    setSearchParams,
    push,
    replace,
    clearSearchParams,
  }
}
